/**
 * Shadery jedwabnych wstęg tła ekranów /auth (makieta `d2-logowanie`, zaakceptowana 2026-10-23).
 * Jeden pełnoekranowy prostokąt; każdy piksel składa N półprzezroczystych wstęg od tyłu do przodu.
 * Wstęga = oś wachlarza (od góry do prawego dołu) + szerokość rosnąca w dół + skręt (cos), a na
 * niej: barwa wzdłuż długości, światło zagięcia, włókna (szum szybki w poprzek, wolny wzdłuż),
 * połysk krawędzi. Krawędź jest ostra w pikselach, nie w procentach szerokości.
 */
export const SILK_VERTEX_SHADER = `
attribute vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }
`;

export const SILK_FRAGMENT_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uResolution;
uniform float uTime;
uniform float uStart; // początek wachlarza na górnej krawędzi, ułamek szerokości
uniform vec3 uSky, uSignal, uAction, uBrand, uAmber;
const int N = 6;

float hash1(float n) { return fract(sin(n * 127.1) * 43758.5453); }
float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float valueNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash2(i), hash2(i + vec2(1.0, 0.0)), f.x),
             mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), f.x), f.y);
}

float spine(float k, float y, float aspect) {
  float x = aspect * uStart + 0.10 * y + aspect * 0.45 * y * y;
  x += (k - 0.5) * (0.04 + 0.34 * y);
  x += 0.040 * sin(y * 3.0 + uTime * 0.30 + k * 2.3) + 0.018 * sin(y * 7.3 - uTime * 0.42 + k * 5.1);
  return x;
}

vec3 colorFrom(int i) {
  if (i == 0) return uSky;
  if (i == 1) return uSignal;
  if (i == 2) return mix(uAmber, vec3(1.0), 0.22);
  if (i == 3) return uAction;
  if (i == 4) return uAmber;
  return uSignal;
}

vec3 colorTo(int i) {
  if (i == 0) return uSignal;
  if (i == 1) return uAction;
  if (i == 2) return uAmber;
  if (i == 3) return uBrand;
  if (i == 4) return mix(uAmber, vec3(1.0), 0.28);
  return mix(uSky, vec3(1.0), 0.25);
}

void main() {
  float aspect = uResolution.x / uResolution.y;
  vec2 pos = vec2(gl_FragCoord.x, uResolution.y - gl_FragCoord.y) / uResolution.y;
  float y = pos.y;
  vec4 dst = vec4(0.0);
  for (int i = 0; i < N; i++) {
    float k = float(i) / float(N - 1);
    float hk = hash1(float(i) + 3.0);
    float xc = spine(k, y, aspect);
    float slope = (spine(k, y + 0.002, aspect) - xc) / 0.002;
    float twist = cos(y * 2.3 + k * 1.9 - uTime * 0.20 + hk * 4.0);
    float width = (0.045 + 0.19 * y) * mix(0.7, 1.3, hk) * (0.30 + 0.70 * abs(twist));
    float d = (pos.x - xc) / sqrt(1.0 + slope * slope);
    float u = d / width;
    float au = abs(u);
    if (au > 1.0) continue; // szum liczony tylko wewnątrz wstęgi
    float side = clamp(twist * 3.0, -1.0, 1.0);
    float along = smoothstep(0.0, 1.0, clamp(y * 1.05 + 0.18 * sin(uTime * 0.12 + k * 3.0) - 0.05, 0.0, 1.0));
    vec3 c = mix(colorFrom(i), colorTo(i), along);
    c *= 0.96 + 0.09 * cos(u * 2.1 + 0.9 * side);
    c *= mix(0.93, 1.0, 0.5 + 0.5 * side);
    float fiber = 0.6 * valueNoise(vec2(d * 780.0 + y * 55.0 + hk * 40.0, y * 4.5 + k * 9.0))
                + 0.4 * valueNoise(vec2(d * 1900.0 + y * 120.0, y * 9.0 + k * 3.0));
    c *= 0.95 + 0.08 * fiber;
    c = mix(c, vec3(1.0), pow(au, 9.0) * 0.30);
    c = mix(c, vec3(1.0), exp(-pow((u - 0.35 * side) * 5.0, 2.0)) * 0.10);
    c = mix(c, mix(uSky, uAmber, 0.5 + 0.5 * sin(y * 6.0 + k * 4.0)), smoothstep(0.86, 1.0, au) * 0.25);
    float edge = max(1.6 / uResolution.y, width * 0.05);
    float alpha = smoothstep(width, width - edge, abs(d)) * mix(0.90, 0.97, hk);
    dst.rgb = c * alpha + dst.rgb * (1.0 - alpha);
    dst.a = alpha + dst.a * (1.0 - alpha);
  }
  gl_FragColor = dst;
}
`;
